import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { DocumentsActions } from '@modeso/aumico-lib-annualreport-fe/actions/workflow-documents.actions';
import { WorkflowDocumentsProvider } from '@modeso/aumico-lib-annualreport-fe/provider/workflow-documets.provider';
import { FilePreviewStatus } from '@modeso/aumico-lib-cpmm-fe/enums/FilePreviewStatus.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { filter, take } from 'rxjs/operators';
import { DocumentSubmissionData } from '../../../../../aumico-components/src/lib/components/ham-document-form/ham-document-form.component';
import { DocumentDto } from '../../dtos/document-dto.interface';

@UntilDestroy()
@Component({
    selector: 'app-documents',
    templateUrl: './documents.component.html',
    styleUrls: ['./documents.component.scss'],
})
export class DocumentsComponent implements OnInit, OnChanges, OnDestroy {
    public showUploadModal = false;
    private files: File[] = [];
    public showAll = false;
    public acceptedExtensions = ['.pdf', '.xlsx', '.docx', '.doc', '.png', '.jpg', '.jpeg'];

    flyoutPosition: { left: number };
    public hoveredDocument: DocumentDto | null = null;
    public previewDocument: DocumentDto | null = null; // Separate property for preview visibility
    public documentToEdit: DocumentDto | null = null; // Document being edited
    public documentToDelete: DocumentDto | null = null; // Document being deleted
    public showDeleteModal = false; // Controls visibility of delete confirmation modal
    public leaveTimer: any;
    private refetchAttempted = new Set<string>();
    private activePollingDocuments = new Set<string>(); // Track which documents have active polling

    @Input() documents: DocumentDto[] = [];
    @Input() documentsInProgressUpload: string[] = [];
    @Input() currentUserId!: string;
    @Input() taskId!: string;
    @Input() projectId!: string;
    @Input() expirationDate: Date;
    @Input() disabled: boolean = false; // Disable all actions if true

    @Output() onDocumentAdded = new EventEmitter<DocumentDto[]>();
    @Output() onDocumentRemoved = new EventEmitter<DocumentDto>();
    @Output() onDocumentsAddedInProgress = new EventEmitter<string[]>();
    @Output() openFile = new EventEmitter<string>();
    @Output() downloadFile = new EventEmitter<{ fileId: string; documentName: string }>();
    @Output() startPolling = new EventEmitter<string>();
    @Output() stopPolling = new EventEmitter<string>();

    get visibleDocuments(): DocumentDto[] {
        return this.showAll
            ? this.documents
            : this.documents.slice(0, Math.max(0, 3 - this.documentsInProgressUpload.length));
    }
    get visibleDocumentsInProgress(): string[] {
        return this.documentsInProgressUpload.slice(0, 3);
    }

    public getPreviewStatusText(status: string): string {
        switch (status) {
            case FilePreviewStatus.NOT_STARTED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_wait_for_anti_virus') as string;
            case FilePreviewStatus.PENDING:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_loading') as string;
            case FilePreviewStatus.GENERATED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_loading') as string;
            case FilePreviewStatus.FAILED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_failed') as string;
            case FilePreviewStatus.NOT_SUPPORTED:
                return this.localizedTexts.t('aumico_app_cpmm_documents_preview_not_supported') as string;
            case FilePreviewStatus.SCANNING:
                return this.localizedTexts.t('aumico_app_cpmm_documents_wait_for_anti_virus') as string;
            default:
                return status;
        }
    }

    constructor(
        private workflowProvider: WorkflowDocumentsProvider,
        private readonly actionListener$: ActionsSubject,
        private readonly localizedTexts: I18NextService,
        private cdr: ChangeDetectorRef, // Add this
    ) { }

    ngOnInit(): void {
        this.actionListener$
            .pipe(ofType(DocumentsActions.uploadDocumentSuccess), untilDestroyed(this))
            .subscribe(() => {
                this.files = [];
                this.showUploadModal = false;
            });

        this.actionListener$
            .pipe(ofType(DocumentsActions.uploadDocumentFailure), untilDestroyed(this))
            .subscribe(({ error }) => {
                // find the document name in the error message and remove it from the documentsInProgressUpload array
                const fileName = this.files.find((f) => error.message.includes(f.name))?.name;
                if (fileName) {
                    this.documentsInProgressUpload = this.documentsInProgressUpload.filter((doc) => doc !== fileName);
                }
            });

        // Listen for document metadata updates
        this.actionListener$
            .pipe(
                ofType(
                    DocumentsActions.updateDocumentMetadataSuccess,
                    DocumentsActions.checkPreviewStatusSuccess,
                    DocumentsActions.loadPreviewUrlSuccess,
                ),
                untilDestroyed(this),
            )
            .subscribe(() => {
                // Force Angular to check for changes
                this.cdr.detectChanges();
                // or use this.cdr.markForCheck() if using OnPush strategy
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['documents']) {
            this.documents.sort((a, b) => {
                const dateA = new Date(a.createdAt).getTime();
                const dateB = new Date(b.createdAt).getTime();
                return dateB - dateA;
            });
            this.stripNameExtensions(this.documents);
        }

        // Update expiration status when either documents or expirationDate changes
        if (changes['documents'] || changes['expirationDate']) {
            this.updateDocumentExpirationStatus();
        }
    }

    private stripNameExtensions(documents: DocumentDto[]): void {
        documents.forEach((document) => {
            if (document.name) {
                document.name = document.name.replace(/\.[^/.]+$/, '');
            }
        });
    }

    private updateDocumentExpirationStatus(): void {
        this.documents.forEach((document) => {
            (document as any).isExpired = this.isExpired(document.expiryDate);
        });
    }

    public openUploadDocumentDialog(): void {
        if (this.disabled) {
            return; // Prevent opening the dialog if disabled
        }
        this.documentToEdit = null;
        this.showUploadModal = true;
    }

    // ADD this new method:
    public onDocumentSubmit(submissionData: DocumentSubmissionData): void {
        const { document, file, isNew } = submissionData;

        // New File
        if (file && isNew) {
            // First emit file name for progress tracking (same as before!)
            this.onDocumentsAddedInProgress.emit([document.name]);

            // Then dispatch upload with document metadata
            this.workflowProvider.dispatchUploadDocument(this.taskId, [file], document);
            this.showUploadModal = false;
            this.documentToEdit = null;
        }

        // New Link
        if (!file && isNew && document.externalLinkUrl) {
            // we dispatch upload with document metadata
            this.workflowProvider.dispatchUploadDocument(this.taskId, [], document);
            this.showUploadModal = false;
            this.documentToEdit = null;
        }

        if (!isNew) {
            this.showUploadModal = false;
            // Existing document being edited
            this.workflowProvider.dispatchUpdateDocumentMetadata(document);
            this.documentToEdit = null;
        }
    }

    public onCancelUpload(): void {
        this.showUploadModal = false;
        this.files = [];
        this.documentToEdit = null;
    }

    public removeDocument(document: DocumentDto): void {
        if (this.disabled) {
            return;
        }
        this.documentToDelete = document;
        this.showDeleteModal = true;
    }

    public editDocumentStart(document: DocumentDto): void {
        if (this.disabled) {
            return;
        }
        this.documentToEdit = document;
        this.showUploadModal = true;
    }

    public onCancelDelete(): void {
        this.showDeleteModal = false;
        this.documentToDelete = null;
    }

    public onConfirmDelete(): void {
        if (this.documentToDelete) {
            this.onDocumentRemoved.emit(this.documentToDelete);
        }
        this.showDeleteModal = false;
        this.documentToDelete = null;
    }

    downloadDocument(document: DocumentDto) {
        if (document.extension === 'link' && document.externalLinkUrl) {
            // For external links, open URL in new tab instead of downloading
            this.openExternalLink(document);
        } else {
            // For files, use the normal download process
            this.downloadFile.emit({ fileId: document.fileId, documentName: document.name });
        }
    }

    public openExternalLink(document: DocumentDto): void {
        if (document.externalLinkUrl) {
            window.open(document.externalLinkUrl, '_blank');
        }
    }

    public openDocument(document: DocumentDto): void {
        this.openFile.emit(document.fileId);
    }

    public onMouseEnter(document: DocumentDto, documentContainer: HTMLElement): void {
        this.clearLeaveTimer();

        // Set hovered document for overlay visibility only
        this.hoveredDocument = document;

        // Set flyout position for potential preview (but don't trigger preview logic)
        const rect = documentContainer.getBoundingClientRect();
        const flyoutWidth = 400;
        const gap = 10;

        this.flyoutPosition = {
            left: rect.left - flyoutWidth - gap,
        };
    }

    public onMouseLeave(): void {
        this.leaveTimer = setTimeout(() => {
            if (this.hoveredDocument) {
                // Clean up any active polling/preview for this document
                this.cleanupPreviewDocument(this.hoveredDocument.fileId);
                this.hoveredDocument = null;
                this.previewDocument = null; // Clear preview as well
            }
        }, 500);
    }

    public handlePreviewImageError(document: DocumentDto): void {
        if (!this.refetchAttempted.has(document.fileId)) {
            this.refetchAttempted.add(document.fileId);
            document.previewUrl = undefined;
            this.workflowProvider.dispatchLoadPreviewUrl(document.fileId);
        }
    }

    public clearLeaveTimer(): void {
        if (this.leaveTimer && this.hoveredDocument) {
            // Clean up any active polling/preview for the currently hovered document
            this.cleanupPreviewDocument(this.hoveredDocument.fileId);
        }
        clearTimeout(this.leaveTimer);
        this.leaveTimer = null;
    }

    public showPreview(document: DocumentDto, documentContainer: HTMLElement): void {
        // Ensure the document is set as hovered and position is calculated
        this.onMouseEnter(document, documentContainer);

        // If there's already a different preview document, clean it up
        if (this.previewDocument && this.previewDocument.fileId !== document.fileId) {
            this.cleanupPreviewDocument(this.previewDocument.fileId);
        }

        // Set the preview document to show the flyout
        this.previewDocument = document;

        // For external links, no need to check preview status or start polling
        if (document.extension === 'link') {
            return;
        }

        // Check local status first to avoid unnecessary network requests
        if (
            document.previewStatus === FilePreviewStatus.GENERATED ||
            document.previewStatus === FilePreviewStatus.FAILED ||
            document.previewStatus === FilePreviewStatus.NOT_SUPPORTED
        ) {
            if (document.previewStatus === FilePreviewStatus.GENERATED && !document.previewUrl) {
                // Status is generated, but we don't have the URL yet. Fetch it.
                this.workflowProvider.dispatchLoadPreviewUrl(document.fileId);
            }
            return;
        }

        const id = document.fileId;
        // initial status check
        this.workflowProvider.dispatchCheckPreviewStatus(id);
        // react to status
        this.actionListener$
            .pipe(
                ofType(DocumentsActions.checkPreviewStatusSuccess),
                filter((a) => a.fileId === id),
                take(1),
                untilDestroyed(this),
            )
            .subscribe(({ status }) => {
                if (status === FilePreviewStatus.GENERATED) {
                    this.workflowProvider.dispatchLoadPreviewUrl(id);
                } else if (status === FilePreviewStatus.PENDING || status === FilePreviewStatus.NOT_STARTED) {
                    this.startPolling.emit(id);
                    this.activePollingDocuments.add(id); // Track that polling started for this document
                }
            });
    }

    private cleanupPreviewDocument(fileId: string): void {
        if (this.activePollingDocuments.has(fileId)) {
            this.stopPolling.emit(fileId);
            this.activePollingDocuments.delete(fileId);
        }
        this.refetchAttempted.delete(fileId);
    }

    isExpireDateAlarming(expiryDate?: Date): boolean {
        if (!expiryDate || !this.expirationDate) {
            return false;
        }
        const date = typeof expiryDate === 'string' ? new Date(expiryDate) : expiryDate;
        return date < this.expirationDate;
    }

    isExpired(expiryDate?: Date): boolean {
        // Convert string to Date if needed
        const date = typeof expiryDate === 'string' ? new Date(expiryDate) : expiryDate;

        const val = date < new Date();
        return val;
    }

    ngOnDestroy(): void {
        // Clean up any active polling when component is destroyed
        this.activePollingDocuments.forEach((fileId) => {
            this.stopPolling.emit(fileId);
        });
        this.activePollingDocuments.clear();

        // Clear any pending timer
        if (this.leaveTimer) {
            clearTimeout(this.leaveTimer);
        }
    }
}
